/**
 * SalesReportsModule Component
 * Sales reporting and analytics
 */
import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SalesReportsModule = ({ onBack }) => {
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [monthFilter, setMonthFilter] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [estimatesRes, invoicesRes] = await Promise.all([
          axios.get(`${API}/sales/estimates`),
          axios.get(`${API}/sales/invoices`)
        ]);
        setEstimates(estimatesRes.data);
        setInvoices(invoicesRes.data);
      } catch (error) {
        console.error('Error loading reports:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const withinRange = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (start && date < start) return false;
    if (end && date > end) return false;
    return true;
  };

  const handleMonthChange = (value) => {
    setMonthFilter(value);
    if (!value) {
      setStartDate('');
      setEndDate('');
      return;
    }
    const [year, month] = value.split('-').map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const filteredEstimates = estimates.filter(est => withinRange(est.date));
  const filteredInvoices = invoices.filter(inv => withinRange(inv.date));

  const estimatesSent = filteredEstimates.filter(est => ['ISSUED', 'APPROVED', 'DECLINED'].includes(est.status)).length;
  const estimatesApproved = filteredEstimates.filter(est => est.status === 'APPROVED').length;
  const totalEstimateValue = filteredEstimates.reduce((sum, est) => sum + (est.total || 0), 0);
  const totalInvoiceValue = filteredInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const totalPaid = filteredInvoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0);
  const totalOutstanding = filteredInvoices.reduce((sum, inv) => sum + (inv.balance_due || 0), 0);
  const acceptanceRate = estimatesSent ? Math.round((estimatesApproved / estimatesSent) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-end">
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Month</span>
              <input
                type="month"
                value={monthFilter}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="sales-reports-month-picker"
              />
            </div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setMonthFilter('');
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg"
              data-testid="sales-reports-start-date"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setMonthFilter('');
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg"
              data-testid="sales-reports-end-date"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="text-center py-12 text-gray-600">Loading reports...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="sales-report-estimates-sent">
              <div className="text-sm text-gray-600">Estimates Sent</div>
              <div className="text-3xl font-bold text-gray-800">{estimatesSent}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="sales-report-estimates-approved">
              <div className="text-sm text-gray-600">Estimates Approved</div>
              <div className="text-3xl font-bold text-green-600">{estimatesApproved}</div>
              <div className="text-xs text-gray-500">Acceptance Rate: {acceptanceRate}%</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="sales-report-estimate-value">
              <div className="text-sm text-gray-600">Estimate Volume</div>
              <div className="text-3xl font-bold text-gray-800">${formatCurrency(totalEstimateValue)}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="sales-report-invoice-value">
              <div className="text-sm text-gray-600">Invoice Amount</div>
              <div className="text-3xl font-bold text-gray-800">${formatCurrency(totalInvoiceValue)}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="sales-report-total-paid">
              <div className="text-sm text-gray-600">Total Paid</div>
              <div className="text-3xl font-bold text-emerald-600">${formatCurrency(totalPaid)}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="sales-report-outstanding">
              <div className="text-sm text-gray-600">Outstanding Balance</div>
              <div className="text-3xl font-bold text-red-600">${formatCurrency(totalOutstanding)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesReportsModule;
